import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import BaseUrl from "../BaseUrl";
import ApiSuffix from "../ApiSuffix";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Button, CircularProgress, TextField, Typography, Paper, Divider, MenuItem } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import "./AccomodationForm.css";

const HOTELS_DATA = [
  { id: 1, name: "Fairfield Marriot", distance: "18.9kms (28 mins)", rate: 9000 },
  { id: 2, name: "Fortune Select Ananda", distance: "9.1 kms (20 mins)", rate: 7500 },
  { id: 3, name: "Kingship by Kahlon", distance: "2.1 kms (6 mins)", rate: 3000 },
];

const AccomodationForm = ({ propSlug }) => {
  const { slug } = useParams();
  const eventSlug = slug;
  const [verifyContact, setVerifyContact] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState({
    verifying: false,
    uploadingPayment: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({});
  const [openThankYou, setOpenThankYou] = useState(false);
  const [delegateId, setDelegateId] = useState(null);
  const [accommodationId, setAccommodationId] = useState(null);
  const paymentFileRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [userData, setUserData] = useState({
    name: "",
    email: "",
    contact: "",
  });

  const [bookingData, setBookingData] = useState({
    selectedHotel: null,
    checkIn: null,
    checkOut: null,
    nights: 0,
    utrNo: "",
    paymentSS: null,
  });

  const validateForm = () => {
    let newErrors = {};

    if (!bookingData.selectedHotel) newErrors.hotel = "Please select hotel";
    if (!bookingData.checkIn) newErrors.checkIn = "Check-in date required";
    if (!bookingData.checkOut) newErrors.checkOut = "Check-out date required";
    if (!bookingData.utrNo) newErrors.utrNo = "UTR number required";
    if (!bookingData.paymentSS) newErrors.paymentSS = "Upload payment screenshot";
    if (bookingData.nights <= 0) newErrors.date = "Check-out must be after check-in";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // 1. Verify Registration
  const handleVerify = async () => {
    if (!verifyContact) {
      setVerifyError("Please enter contact number");
      return;
    }

    setLoading((prev) => ({ ...prev, verifying: true }));
    setVerifyError("");

    const url = BaseUrl.dev + ApiSuffix.getContact + verifyContact;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (data && data.contact) {
        setUserData({
          name: `${data.first_name} ${data.last_name}`,
          email: data.email,
          contact: data.contact,
        });

        const dId = data.delegate_id || data.id;
        setDelegateId(dId);
        setIsVerified(true);
      } else {
        setVerifyError("Registration not found.");
      }
    } catch (err) {
      setVerifyError("Verification failed. Try again.");
    } finally {
      setLoading((prev) => ({ ...prev, verifying: false }));
    }
  };
  // 2. Date Calculation Logic
  useEffect(() => {
    if (bookingData.checkIn && bookingData.checkOut) {
      const start = new Date(bookingData.checkIn);
      const end = new Date(bookingData.checkOut);

      const diffTime = end - start;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      setBookingData((prev) => ({
        ...prev,
        nights: diffDays > 0 ? diffDays : 0,
      }));
    }
  }, [bookingData.checkIn, bookingData.checkOut]);

  const baseAmount = (bookingData.selectedHotel?.rate || 0) * bookingData.nights;
  const gstAmount = (baseAmount * 18) / 100;
  const totalAmount = baseAmount + gstAmount;

  // 3. File Upload (Same as Registration)
  const onFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading((prev) => ({ ...prev, uploadingPayment: true }));
    const formDataUpload = new FormData();
    formDataUpload.append("image", file);
    formDataUpload.append("fileName", `acc-payment-${userData.contact}`);

    try {
      const response = await fetch("https://satyasolar.pythonanywhere.com/api/upload-doc/", {
        method: "POST",
        body: formDataUpload,
      });
      const data = await response.json();
      setBookingData((prev) => ({ ...prev, paymentSS: data }));
    } catch (error) {
      alert("File upload failed");
    } finally {
      setLoading((prev) => ({ ...prev, uploadingPayment: false }));
      if (paymentFileRef.current) paymentFileRef.current.value = null;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError("");

    if (!eventSlug) {
      setSubmitError("Event slug missing in URL. Please refresh.");
      setSubmitting(false);
      return;
    }

    const payload = {
      slug: eventSlug,
      delegate_id: delegateId,
      name: userData.name,
      email: userData.email,
      contact: userData.contact,
      selectedHotel: bookingData.selectedHotel.name,
      checkIn: bookingData.checkIn,
      checkOut: bookingData.checkOut,
      nights: bookingData.nights,
      totalAmount: totalAmount,
      utrNo: bookingData.utrNo,
      paymentSS: bookingData.paymentSS,
    };

    try {
      const response = await fetch("https://neurosurgery.pythonanywhere.com/api/book-accommodation/", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.error === false) {
        setAccommodationId(data.accommodation_id);
        setOpenThankYou(true);
        resetForm();
      } else {
        setSubmitError(data.error_msg || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error("API ERROR:", err);
      setSubmitError("Network error. Please check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setBookingData({
      selectedHotel: null,
      checkIn: null,
      checkOut: null,
      nights: 0,
      utrNo: "",
      paymentSS: null,
    });
    setIsVerified(false);
    setVerifyContact("");
  };

  return (
    <>
      <div className="acc-container">
        <div className="acc-wrapper">
          <div className="acc-header-section">
            <h2>Accommodation Booking</h2>
            <p>To facilitate a comfortable stay during E’MISS–CVJ 2026, please book your stay below.</p>
          </div>

          <Paper elevation={3} className="acc-form-card">
            {!isVerified ? (
              <div className="acc-verify-box">
                <Typography variant="h6">Registration Verification</Typography>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Enter Registered Contact Number"
                  value={verifyContact}
                  error={Boolean(verifyError)}
                  helperText={verifyError}
                  onChange={(e) => setVerifyContact(e.target.value)}
                />
                <Button color="primary" variant="contained" onClick={handleVerify} disabled={loading.verifying}>
                  {loading.verifying ? <CircularProgress size={24} color="inherit" /> : "Verify Registration"}
                </Button>
              </div>
            ) : (
              <div className="acc-main-flow">
                <div className="acc-prefilled-fields">
                  <TextField label="Name" value={userData.name} fullWidth disabled />
                  <TextField label="Email" value={userData.email} fullWidth disabled />
                  <TextField label="Contact" value={userData.contact} fullWidth disabled />
                </div>

                <Divider sx={{ my: 3 }}>Select Hotel</Divider>

                <div className="acc-hotel-grid">
                  <TextField
                    select
                    fullWidth
                    label="Select Hotel"
                    error={!!errors.hotel}
                    helperText={errors.hotel}
                    value={bookingData.selectedHotel?.id || ""}
                    onChange={(e) => {
                      const hotel = HOTELS_DATA.find((h) => h.id === Number(e.target.value));
                      setBookingData((prev) => ({
                        ...prev,
                        selectedHotel: hotel,
                        checkIn: null,
                        checkOut: null,
                        nights: 0,
                      }));
                    }}>
                    {HOTELS_DATA.map((hotel) => (
                      <MenuItem key={hotel.id} value={hotel.id}>
                        {hotel.name} - ₹{hotel.rate}/night
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                {bookingData.selectedHotel && (
                  <div className="acc-booking-details">
                    <div className="acc-date-row">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Check-in Date"
                          value={bookingData.checkIn ? dayjs(bookingData.checkIn) : null}
                          minDate={dayjs()}
                          onChange={(value) =>
                            setBookingData((prev) => ({
                              ...prev,
                              checkIn: value ? value.format("YYYY-MM-DD") : "",
                              checkOut: null,
                              nights: 0,
                            }))
                          }
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                        <DatePicker
                          label="Check-out Date"
                          value={bookingData.checkOut ? dayjs(bookingData.checkOut) : null}
                          minDate={
                            bookingData.checkIn ? dayjs(bookingData.checkIn).add(1, "day") : dayjs().add(1, "day")
                          }
                          onChange={(value) =>
                            setBookingData((prev) => ({
                              ...prev,
                              checkOut: value ? value.format("YYYY-MM-DD") : "",
                            }))
                          }
                          slotProps={{ textField: { fullWidth: true } }}
                        />

                        <TextField label="Nights" value={bookingData.nights} disabled />
                      </LocalizationProvider>
                    </div>

                    <div className="acc-payment-section">
                      <div className="acc-calc-box">
                        <Typography variant="body1">Room Rate: ₹{bookingData.selectedHotel.rate}</Typography>
                        <Typography variant="body1">Nights: {bookingData.nights}</Typography>
                        <Typography variant="h6">
                          Total: ₹{baseAmount} + 18% GST = ₹{totalAmount}
                        </Typography>
                      </div>

                      <div className="acc-qr-area">
                        <img src="/qr-code.png" alt="Payment QR" className="acc-qr-img" />
                        <div className="acc-bank-info">
                          <p>
                            <strong>Bank:</strong> State Bank of India
                          </p>
                          <p>
                            <strong>A/C:</strong> 37963478309
                          </p>
                          <p>
                            <strong>IFSC:</strong> SBIN0007789
                          </p>
                        </div>
                      </div>

                      <div className="acc-upload-box">
                        <TextField
                          fullWidth
                          label="UTR No."
                          error={!!errors.utrNo}
                          helperText={errors.utrNo}
                          value={bookingData.utrNo}
                          onChange={(e) => setBookingData((prev) => ({ ...prev, utrNo: e.target.value }))}
                        />

                        <input
                          type="file"
                          ref={paymentFileRef}
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={onFileChange}
                        />

                        {loading.uploadingPayment ? (
                          <Button variant="contained" disabled>
                            <CircularProgress size={24} />
                          </Button>
                        ) : bookingData.paymentSS ? (
                          <div className="uploaded-file" style={{ display: "flex", gap: "10px" }}>
                            <Button
                              variant="outlined"
                              component="a"
                              href={bookingData.paymentSS}
                              target="_blank"
                              rel="noopener noreferrer">
                              View File
                            </Button>

                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setBookingData((prev) => ({ ...prev, paymentSS: null }));
                                if (paymentFileRef.current) paymentFileRef.current.value = null;
                              }}>
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <Button variant="contained" onClick={() => paymentFileRef.current.click()}>
                            Upload Payment Screenshot
                          </Button>
                        )}

                        {errors.paymentSS && (
                          <Typography color="error" variant="body2">
                            {errors.paymentSS}
                          </Typography>
                        )}
                      </div>
                    </div>

                    {submitError && (
                      <Typography color="error" sx={{ mt: 2 }}>
                        {submitError}
                      </Typography>
                    )}
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{ mt: 3 }}
                      onClick={handleSubmit}
                      disabled={submitting || !bookingData.paymentSS}>
                      {submitting ? <CircularProgress size={24} /> : "Proceed to Booking"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Paper>

          <div className="acc-notes">
            <p>* Accommodation is available only for registered delegates.</p>
            <p>* Rooms are limited and allotted on a first-come, first-served basis.</p>
            <p>* Booking will be confirmed only after successful payment.</p>
            <p>* Room allocation will be subject to availability at the chosen hotel.</p>
          </div>
        </div>
      </div>
      <Dialog open={openThankYou} onClose={() => setOpenThankYou(false)}>
        <DialogTitle>Booking Submitted</DialogTitle>
        <DialogContent>
          <Typography>Your accommodation request has been successfully submitted.</Typography>

          <Typography sx={{ mt: 2 }}>
            <strong>Booking ID:</strong>
            {accommodationId}
          </Typography>

          <Typography sx={{ mt: 1 }}>Please save this ID for future reference.</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenThankYou(false)} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccomodationForm;
